import { tt } from '@agos/shared'
import type { Column } from '@agos/ui'

import { formatDuration, formatTime, sheetDate, workedMs, type AttendanceRecord } from './time-sheet'

/**
 * The columns of *Mesačný prehľad* — FRONTEND.md §9.213.
 *
 * Legacy's five, with legacy's sort values. A module of its own rather than a literal inside the
 * component because **the interesting part is what each column sorts by**, and that is exactly what
 * a browser test cannot see: on the restored data 92 of July's 100 closed shifts run ten hours or
 * more, so within one employee the text order and the duration order usually agree, and an e2e
 * asserting the rendered order passes either way. It did — the first version of this survived its own
 * mutation.
 */

/**
 * The record shape these columns draw.
 *
 * An alias for the module's own `AttendanceRecord` rather than a narrower copy: a structural twin
 * would compile and then quietly stop matching the day somebody adds a field, and the sort values
 * here read fields the real record owns.
 */
export type MonthRecord = AttendanceRecord

/**
 * How long the shift ran, in milliseconds — what *Hodiny* sorts by.
 *
 * **Not the rendered text.** `3:05` sorts after `12:00` as a string and before it as a length of
 * time, and *who worked most* means the second. A record missing either stamp is nought rather than
 * absent: an open shift is a real row somebody has to correct, and dropping it out of the sort is how
 * it stays uncorrected.
 */
export function hoursSortValue(entry: MonthRecord): number {
  // `?? 0` on purpose. `workedMs` answers null for a shift with a stamp missing, and `DataTable`
  // sorts a null to the end **whichever direction is asked for** — so an open shift would be last
  // ascending and last descending, which is how it stays uncorrected. Nought puts it at the
  // nothing-worked end, where somebody looking for exactly that will find it.
  return workedMs(entry) ?? 0
}

export function monthColumns(): Column<MonthRecord>[] {
  return [
    {
      key: 'date',
      header: tt('Dátum'),
      sortable: true,
      sortValue: (entry) => entry.workDate ?? '',
      render: (entry) => sheetDate(entry.workDate),
    },
    {
      key: 'checkIn',
      header: tt('Príchod'),
      sortable: true,
      sortValue: (entry) => entry.checkIn ?? '',
      className: 'text-center font-mono',
      headerClassName: 'text-center',
      render: (entry) => formatTime(entry.checkIn),
    },
    {
      key: 'checkOut',
      header: tt('Odchod'),
      sortable: true,
      sortValue: (entry) => entry.checkOut ?? '',
      className: 'text-center font-mono',
      headerClassName: 'text-center',
      render: (entry) => formatTime(entry.checkOut),
    },
    {
      key: 'hours',
      header: tt('Hodiny'),
      sortable: true,
      sortValue: hoursSortValue,
      className: 'text-center font-mono',
      headerClassName: 'text-center',
      render: (entry) => formatDuration(workedMs(entry)),
    },
    {
      key: 'notes',
      header: tt('Poznámka'),
      sortable: true,
      sortValue: (entry) => entry.notes ?? '',
      className: 'text-xs text-muted-foreground',
      render: (entry) => entry.notes ?? '',
    },
  ]
}
